import { getPlantById } from '../../Services/serviceURL';
import { GUID_REGEX, parseJsonSafely } from './createPlantHelpers';
import { AssetDetails, PlantData } from './createPlantDetailsModel';

export const EMPTY_CREATE_PLANT_DETAILS_DATA: PlantData = {
  customerId: '',
  customerName: '',
  siteId: '',
  siteName: '',
  plantName: '',
  plantStatus: 'Active',
  assets: [],
};

function resolveGuidForPlantFetch(plantGuid: string | null): string | null {
  if (plantGuid) {
    return plantGuid;
  }

  const storedPlantData = localStorage.getItem('currentPlantData');
  if (!storedPlantData) {
    return null;
  }

  const parsedData = parseJsonSafely<{ plantId?: string }>(storedPlantData);
  return parsedData?.plantId ?? null;
}

async function fetchPlantRecordFromApi(guid: string): Promise<Record<string, unknown> | null> {
  if (!GUID_REGEX.test(guid)) {
    return null;
  }

  try {
    const response = await getPlantById(guid);
    if (response.data.Success && response.data.Data) {
      return response.data.Data as Record<string, unknown>;
    }
  } catch (apiError) {
    console.error('Plant details API fetch failed:', apiError);
  }

  return null;
}

function loadPlantRecordFromLocalStorage(): Record<string, unknown> | null {
  const storedPlantData = localStorage.getItem('currentPlantData');
  if (!storedPlantData) {
    return null;
  }

  const parsed = parseJsonSafely<Record<string, unknown>>(storedPlantData);
  if (!parsed) {
    console.error('Error parsing stored plant data for plant details');
    return null;
  }

  if (!parsed.assets) {
    parsed.assets = [];
  }

  return parsed;
}

export function mapPlantAssetsToHierarchy(assets: unknown[]): AssetDetails[] {
  return assets.map((asset) => {
    const record = asset as Record<string, unknown>;
    return {
      id: (record.id as string) || crypto.randomUUID(),
      type: String(record.type ?? ''),
      name: String(record.name ?? ''),
      status: String(record.status ?? ''),
      parameters: (record.parameters as AssetDetails['parameters']) || [],
      gatewayDeviceType: String(record.gatewayDeviceType ?? ''),
      gatewayDeviceId: String(record.gatewayDeviceId ?? ''),
      simNumber: String(record.simNumber ?? ''),
      controllerType: String(record.controllerType ?? ''),
      controllerDeviceId: String(record.controllerDeviceId ?? ''),
    };
  });
}

export async function loadCreatePlantDetailsData(): Promise<{
  plantData: PlantData;
  assetHierarchy: AssetDetails[];
}> {
  const plantGuid = localStorage.getItem('currentplantguid');
  const guidToUse = resolveGuidForPlantFetch(plantGuid);

  let data: Record<string, unknown> | null = null;
  if (guidToUse) {
    data = await fetchPlantRecordFromApi(guidToUse);
  }

  if (!data) {
    data = loadPlantRecordFromLocalStorage();
  }

  if (!data) {
    return {
      plantData: EMPTY_CREATE_PLANT_DETAILS_DATA,
      assetHierarchy: [],
    };
  }

  const plantData = data as unknown as PlantData;
  const customerId = plantData.customerId;
  if (customerId) {
    localStorage.setItem('customerid', customerId);
  }

  const rawAssets = Array.isArray(data.assets) ? data.assets : [];
  return {
    plantData,
    assetHierarchy: mapPlantAssetsToHierarchy(rawAssets),
  };
}
